import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, 
  CheckCircle2, 
  ChevronDown, 
  ChevronUp, 
  X, 
  Layers, 
  ShoppingBag,
  Palette, 
  QrCode, 
  ExternalLink, 
  ArrowRight,
  Sparkles,
  PartyPopper
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useVenues } from '../../hooks/useVenues';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { supabase } from '../../lib/supabase';
import { WelcomeOnboardingModal } from './WelcomeOnboardingModal';
import { getPlatformMenuUrl } from '../../lib/utils';

export const OnboardingWidget: React.FC = () => {
  const { activeVenue } = useVenues();
  const { canAccessCategories, isProPlus, tier } = useRoleAccess();
  const venueId = activeVenue?.id || 'default';

  // 1-time Welcome Banner state
  const [showWelcomeNotice, setShowWelcomeNotice] = useState(false);

  // Interactive Welcome Onboarding Wizard modal state
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Onboarding Widget state
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({});

  // Real backend setup counts from Supabase
  const [itemsCount, setItemsCount] = useState<number | null>(null);
  const [categoriesCount, setCategoriesCount] = useState<number | null>(null);
  const [isCheckingReadiness, setIsCheckingReadiness] = useState<boolean>(true);

  // Check 1-time account creation notice on mount
  useEffect(() => {
    const justRegistered = sessionStorage.getItem('tg_just_registered');
    const noticeFlag = localStorage.getItem('tg_account_created_notice');

    if (justRegistered === 'true' || noticeFlag === 'true') {
      setShowWelcomeNotice(true);
      sessionStorage.removeItem('tg_just_registered');
      localStorage.removeItem('tg_account_created_notice');
    }
  }, []);

  // Opens the Welcome Onboarding Wizard when explicitly queued by venue creation
  useEffect(() => {
    if (!activeVenue?.id) return;
    const targetVenueId = sessionStorage.getItem('tg_show_onboarding_wizard');
    if (targetVenueId === activeVenue.id || targetVenueId === 'true') {
      setIsWizardOpen(true);
      sessionStorage.removeItem('tg_show_onboarding_wizard');
    }
  }, [activeVenue?.id]);

  // Inspect live database catalog for the active venue to detect real readiness
  useEffect(() => {
    let isMounted = true;
    const backendOnboarding = activeVenue?.theme_settings?.onboarding;
    if (!activeVenue?.id || backendOnboarding?.dismissed) {
      if (backendOnboarding?.dismissed) setIsDismissed(true);
      setIsCheckingReadiness(false);
      return;
    }

    const checkVenueReadiness = async () => {
      try {
        const [itemsRes, catsRes] = await Promise.all([
          supabase
            .from('items')
            .select('id', { count: 'exact', head: true })
            .eq('venue_id', activeVenue.id),
          supabase
            .from('categories')
            .select('id', { count: 'exact', head: true })
            .eq('venue_id', activeVenue.id),
        ]);

        if (!isMounted) return;

        setItemsCount(itemsRes.count ?? 0);
        setCategoriesCount(catsRes.count ?? 0);
      } catch (err) {
        console.warn('[OnboardingWidget] Failed to verify venue readiness:', err);
      } finally {
        if (isMounted) {
          setIsCheckingReadiness(false);
        }
      }
    };

    void checkVenueReadiness();
    return () => {
      isMounted = false;
    };
  }, [activeVenue?.id]);

  // If a venue has 0 menu items and has not yet seen the onboarding wizard in this session, trigger it automatically
  useEffect(() => {
    if (!activeVenue?.id || isCheckingReadiness || itemsCount === null) return;
    const sessionSeenKey = `tg_wizard_seen_${activeVenue.id}`;
    const hasSeenSessionWizard = sessionStorage.getItem(sessionSeenKey);
    if (itemsCount === 0 && !hasSeenSessionWizard && !isDismissed) {
      setIsWizardOpen(true);
      sessionStorage.setItem(sessionSeenKey, 'true');
    }
  }, [activeVenue?.id, isCheckingReadiness, itemsCount, isDismissed]);

  // Load saved onboarding state: prefers backend venue theme_settings, falls back to local storage
  useEffect(() => {
    if (!venueId || venueId === 'default') return;

    const backendOnboarding = activeVenue?.theme_settings?.onboarding;
    if (backendOnboarding) {
      // If legacy bug auto-dismissed the checklist but the venue has 0 items, recover and show the checklist
      const isStaleDismissal = backendOnboarding.dismissed && itemsCount === 0;
      setCompletedSteps(isStaleDismissal ? {} : (backendOnboarding.completed_steps || {}));
      setIsMinimized(Boolean(backendOnboarding.minimized));
      setIsDismissed(isStaleDismissal ? false : Boolean(backendOnboarding.dismissed));
      return;
    }

    try {
      const saved = localStorage.getItem(`tg_onboarding_${venueId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        const isStaleDismissal = parsed.isDismissed && itemsCount === 0;
        setCompletedSteps(isStaleDismissal ? {} : (parsed.completedSteps || {}));
        setIsMinimized(Boolean(parsed.isMinimized));
        setIsDismissed(isStaleDismissal ? false : Boolean(parsed.isDismissed));
      } else {
        setCompletedSteps({});
        setIsMinimized(false);
        setIsDismissed(false);
      }
    } catch {
      // Ignore parse error
    }
  }, [venueId, activeVenue?.theme_settings?.onboarding, itemsCount]);

  // Step 1: Menu (Requires at least 1 food or beverage item created in catalog)
  const hasMenuSetup = itemsCount !== null && itemsCount > 0;

  // Step 2: Branding (Has customized logo, cover, custom colors, or theme preset)
  const hasThemeSetup = Boolean(
    activeVenue?.logo_url ||
    activeVenue?.cover_url ||
    (activeVenue?.primary_color && activeVenue.primary_color.toLowerCase() !== '#ff4f00') ||
    activeVenue?.theme_settings?.theme_preset ||
    activeVenue?.font_family
  );

  // Step 3: QR & Go Live (Has active slug, menu setup started, and either custom QR styling or manual verification)
  const hasQrSetup = Boolean(
    activeVenue?.slug &&
    hasMenuSetup &&
    (activeVenue?.theme_settings?.qr_styling || completedSteps.qr)
  );

  // Combines real database readiness with any manual user checklist overrides
  const isStepCompleted = (stepId: string): boolean => {
    if (completedSteps[stepId]) return true;
    if (stepId === 'menu') return hasMenuSetup;
    if (stepId === 'theme') return hasThemeSetup;
    if (stepId === 'qr') return hasQrSetup;
    return false;
  };

  // Save onboarding state both in local storage cache and directly to the backend database
  const saveState = (updatedSteps: Record<string, boolean>, minimized: boolean, dismissed: boolean) => {
    setCompletedSteps(updatedSteps);
    setIsMinimized(minimized);
    setIsDismissed(dismissed);

    // 1. Instant local storage cache
    try {
      localStorage.setItem(
        `tg_onboarding_${venueId}`,
        JSON.stringify({ completedSteps: updatedSteps, isMinimized: minimized, isDismissed: dismissed })
      );
    } catch {
      // Ignore storage error
    }

    // 2. Persist to backend database without schema changes (via existing venue theme_settings JSONB)
    if (activeVenue?.id) {
      const currentThemeSettings = activeVenue.theme_settings || {};
      void supabase
        .from('venues')
        .update({
          theme_settings: {
            ...currentThemeSettings,
            onboarding: {
              completed_steps: updatedSteps,
              minimized,
              dismissed,
              updated_at: new Date().toISOString(),
            },
          },
        })
        .eq('id', activeVenue.id);
    }
  };

  const toggleStep = (stepId: string) => {
    const currentlyDone = isStepCompleted(stepId);
    const next = { ...completedSteps, [stepId]: !currentlyDone };
    saveState(next, isMinimized, isDismissed);
  };

  const publicMenuPreviewUrl = activeVenue?.slug 
    ? `${getPlatformMenuUrl()}/${activeVenue.slug}/${activeVenue.default_locale || 'en-gb'}` 
    : null;

  const steps = [
    {
      id: 'menu',
      title: canAccessCategories ? 'Add Menu Categories & Items' : 'Add Menu Items',
      description: canAccessCategories 
        ? 'Organize your menu sections and food items.' 
        : 'Add and manage your food & beverage offerings.',
      icon: canAccessCategories ? Layers : ShoppingBag,
      links: [
        ...(canAccessCategories ? [{ label: 'Categories', path: '/admin/categories' }] : []),
        { label: 'Menu Items', path: '/admin/items' }
      ]
    },
    {
      id: 'theme',
      title: 'Customize Venue Branding',
      description: 'Set your brand colors, logo, and presentation.',
      icon: Palette,
      links: [
        { label: 'Theme Settings', path: '/admin/theme' }
      ]
    },
    {
      id: 'qr',
      title: 'Get Your QR Code & Go Live',
      description: 'Download table QR codes and preview your live menu.',
      icon: QrCode,
      links: [
        { label: 'QR Manager', path: '/admin/qr' }
      ],
      previewUrl: publicMenuPreviewUrl
    }
  ];

  const totalSteps = steps.length;
  const completedCount = steps.filter(s => isStepCompleted(s.id)).length;
  const isFullySetup = completedCount === totalSteps;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  // Automatically record dismissal in both backend database and local storage only when a venue actually has items and is 100% complete
  useEffect(() => {
    if (isFullySetup && venueId && venueId !== 'default' && !isDismissed && itemsCount !== null && itemsCount > 0) {
      saveState({ menu: true, theme: true, qr: true }, false, true);
    }
  }, [isFullySetup, venueId, isDismissed, itemsCount]);

  const dismissWelcomeNotice = () => {
    setShowWelcomeNotice(false);
  };

  // Do not render the onboarding floating card if verifying readiness, if dismissed, or if the venue is already 100% set up
  if (isCheckingReadiness || isDismissed || isFullySetup) {
    return (
      <>
        {/* Full-screen Interactive Setup Guide */}
        <WelcomeOnboardingModal
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          venue={activeVenue}
          itemsCount={itemsCount}
          categoriesCount={categoriesCount}
          hasMenuSetup={hasMenuSetup}
          hasThemeSetup={hasThemeSetup}
          hasQrSetup={hasQrSetup}
        />

        <AnimatePresence>
          {showWelcomeNotice && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed top-5 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-xl bg-gradient-to-r from-emerald-950/90 via-brand-surface/95 to-emerald-950/90 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-4 shadow-[0_10px_35px_rgba(16,185,129,0.25)] text-white"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                    <PartyPopper size={22} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-sm text-white tracking-tight">Account & Venue Created Successfully!</h4>
                      <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase px-2 py-0.5 rounded-full border border-emerald-500/30 shrink-0">
                        Live
                      </span>
                    </div>
                    <p className="text-xs text-gray-300 truncate mt-0.5">
                      Welcome aboard! Your TableGreet HQ is ready. Follow the launch checklist below to publish your menu.
                    </p>
                  </div>
                </div>
                <button
                  onClick={dismissWelcomeNotice}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                  aria-label="Dismiss notice"
                >
                  <X size={18} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      {/* Full-screen Interactive Setup Guide */}
      <WelcomeOnboardingModal
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        venue={activeVenue}
        itemsCount={itemsCount}
        categoriesCount={categoriesCount}
        hasMenuSetup={hasMenuSetup}
        hasThemeSetup={hasThemeSetup}
        hasQrSetup={hasQrSetup}
      />

      {/* 1-Time Account Successfully Created Banner */}
      <AnimatePresence>
        {showWelcomeNotice && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed top-5 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-xl bg-gradient-to-r from-emerald-950/90 via-brand-surface/95 to-emerald-950/90 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-4 shadow-[0_10px_35px_rgba(16,185,129,0.25)] text-white"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                  <PartyPopper size={22} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-sm text-white tracking-tight">Account & Venue Created Successfully!</h4>
                    <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase px-2 py-0.5 rounded-full border border-emerald-500/30 shrink-0">
                      Live
                    </span>
                  </div>
                  <p className="text-xs text-gray-300 truncate mt-0.5">
                    Welcome aboard! Your TableGreet HQ is ready. Follow the launch checklist below to publish your menu.
                  </p>
                </div>
              </div>
              <button
                onClick={dismissWelcomeNotice}
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                aria-label="Dismiss notice"
              >
                <X size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom-Right Go Live Onboarding Widget */}
      <AnimatePresence>
        {!isDismissed && (
          <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end">
            {isMinimized ? (
              /* Minimized Floating Pill */
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={() => saveState(completedSteps, false, false)}
                className="group flex items-center gap-3 bg-brand-surface/95 backdrop-blur-xl border border-brand-primary/40 hover:border-brand-primary p-3 pr-4 rounded-full shadow-[0_10px_30px_rgba(255,79,0,0.25)] text-white transition-all hover:scale-105"
              >
                <div className="relative w-8 h-8 rounded-full bg-brand-primary/20 flex items-center justify-center text-brand-primary font-black text-xs border border-brand-primary/30">
                  <Rocket size={16} className="text-brand-primary animate-pulse" />
                </div>
                <div className="flex flex-col text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xs tracking-tight text-white group-hover:text-brand-primary transition-colors">
                      Go Live Checklist
                    </span>
                    <span className="bg-brand-primary/20 text-brand-primary text-[10px] font-black px-1.5 py-0.5 rounded-md border border-brand-primary/30">
                      {completedCount}/{totalSteps}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-medium">
                    {progressPercent === 100 ? 'Ready for launch! 🎉' : `${progressPercent}% completed`}
                  </span>
                </div>
                <ChevronUp size={16} className="text-gray-400 group-hover:text-white transition-colors" />
              </motion.button>
            ) : (
              /* Expanded Card */
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="w-80 md:w-96 bg-brand-surface/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-5 text-white"
              >
                {/* Widget Header */}
                <div className="flex items-start justify-between gap-3 pb-3 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-brand-primary/10 border border-brand-primary/30 flex items-center justify-center text-brand-primary shrink-0">
                      <Rocket size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm text-white tracking-tight">Go Live Checklist</h3>
                        <span className="text-[10px] font-black bg-brand-primary/20 text-brand-primary border border-brand-primary/30 px-2 py-0.5 rounded-full">
                          {completedCount}/{totalSteps}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                        Crucial steps to launch your digital menu
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setIsWizardOpen(true)}
                      className="p-1 rounded-lg text-gray-400 hover:text-brand-primary hover:bg-brand-primary/10 transition-colors cursor-pointer"
                      title="Launch full setup guide"
                    >
                      <Sparkles size={18} />
                    </button>
                    <button
                      onClick={() => saveState(completedSteps, true, false)}
                      className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                      title="Minimize checklist"
                    >
                      <ChevronDown size={18} />
                    </button>
                    <button
                      onClick={() => saveState(completedSteps, false, true)}
                      className="p-1 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer"
                      title="Dismiss checklist"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="my-3">
                  <div className="flex items-center justify-between text-[11px] font-semibold text-gray-400 mb-1.5">
                    <span>Overall Readiness</span>
                    <span className="text-brand-primary font-bold">{progressPercent}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercent}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-brand-primary to-orange-400 rounded-full shadow-[0_0_10px_rgba(255,79,0,0.5)]"
                    />
                  </div>
                </div>

                {/* Steps List */}
                <div className="space-y-3 mt-4">
                  {steps.map((step, idx) => {
                    const isDone = isStepCompleted(step.id);
                    const StepIcon = step.icon;

                    return (
                      <div
                        key={step.id}
                        className={`p-3 rounded-xl border transition-all ${
                          isDone
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-white/[0.02] border-white/5 hover:border-white/15'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => toggleStep(step.id)}
                            className={`mt-0.5 shrink-0 w-5 h-5 rounded-lg border flex items-center justify-center transition-colors ${
                              isDone
                                ? 'bg-emerald-500 border-emerald-500 text-black'
                                : 'border-gray-500 hover:border-brand-primary text-transparent'
                            }`}
                            title={isDone ? 'Mark as incomplete' : 'Mark as complete'}
                          >
                            <CheckCircle2 size={14} className={isDone ? 'text-black' : 'opacity-0'} />
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span
                                className={`text-xs font-bold tracking-tight cursor-pointer ${
                                  isDone ? 'line-through text-gray-400' : 'text-white'
                                }`}
                                onClick={() => toggleStep(step.id)}
                              >
                                {idx + 1}. {step.title}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-400 leading-normal mt-0.5">
                              {step.description}
                            </p>

                            {/* Action Links */}
                            <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-white/5">
                              {step.links.map((link) => (
                                <Link
                                  key={link.path}
                                  to={link.path}
                                  className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-primary hover:text-orange-300 transition-colors bg-brand-primary/10 hover:bg-brand-primary/20 border border-brand-primary/20 px-2.5 py-1 rounded-lg"
                                >
                                  <span>{link.label}</span>
                                  <ArrowRight size={12} />
                                </Link>
                              ))}

                              {step.previewUrl && (
                                <a
                                  href={step.previewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:text-emerald-300 transition-colors bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-2.5 py-1 rounded-lg"
                                >
                                  <span>Preview Menu</span>
                                  <ExternalLink size={12} />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer status */}
                <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[10px] text-gray-400">
                  <button
                    type="button"
                    onClick={() => setIsWizardOpen(true)}
                    className="flex items-center gap-1.5 font-bold text-brand-primary hover:text-orange-300 transition-colors cursor-pointer"
                  >
                    <Sparkles size={12} />
                    <span>Launch Setup Guide</span>
                  </button>
                  {progressPercent === 100 ? (
                    <span className="font-bold text-emerald-400">🎉 Ready to publish!</span>
                  ) : (
                    <span className="text-gray-400">{progressPercent}% complete</span>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
